import { useState, useEffect, useMemo } from "react";
import { X, PlusIcon, Loader } from "lucide-react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { useListCustomers } from "@/lib/api/customer";
import { Customer } from "@/lib/api/reports";
import { SelectOptionItem } from "@/lib/models/ui";
import {
  CreateChatStreamValidation,
  ChatCreateSource,
  ChatType,
  TwilioMessageName,
  SourcePhoneNumberType,
  ChatStream
} from "@/lib/api/chats";

interface SelectedCustomer {
  customer: Customer;
  phoneNumbers: string[];
}

export interface ChatStreamFormData {
  name: string;
  create_source: ChatCreateSource;
  chat_type: ChatType.SOURCE_FAN_OUT_ONLY;
  message_service_env: string;
  participants: any[];
  customer_phone_type: SourcePhoneNumberType;
}

export interface ChatStreamFormProps {
  mode: 'create' | 'update' | 'preview';
  defaultValues?: {
    name?: string;
    customerIds?: string[];
    phoneNumbers?: string[];
    chatStream?: ChatStream;
  };
  onSubmit?: (data: ChatStreamFormData) => Promise<void>;
  isSubmitting?: boolean;
  readOnly?: boolean;
  children?: React.ReactNode;
}

export function ChatStreamForm({
  mode,
  defaultValues = {},
  onSubmit,
  isSubmitting = false,
  readOnly = false,
  children
}: ChatStreamFormProps) {
  const { toast } = useToast();
  const customersResult = useListCustomers();

  const isDisabled = readOnly || mode === 'preview' || isSubmitting;

  const [formData, setFormData] = useState({
    name: defaultValues.name || "",
  });

  const [manualPhoneNumbers, setManualPhoneNumbers] = useState<string[]>(
    defaultValues.phoneNumbers || []
  );
  const [currentPhoneInput, setCurrentPhoneInput] = useState("");
  const [selectedCustomersIds, setSelectedCustomerIds] = useState<string[]>(defaultValues.customerIds || []);
  const [customerSearchValue, setCustomerSearchValue] = useState("");

  const customers: Customer[] = customersResult?.data?.data || [];

  const selectedCustomerMap = selectedCustomersIds.reduce((acc: Record<string, Customer>, item: string) => {
    const customer = customers.find(customer => customer.id === item);
    if (!customer) return acc;

    acc[item] = customer!;
    return acc;
  }, {});

  const selectedCustomers: Customer[] = useMemo(() => {
    return customers.filter((customer: Customer) => selectedCustomersIds.includes(customer.id));
  }, [customers, selectedCustomersIds]);

  const customerOptions: SelectOptionItem<string>[] = customers.map((customer: Customer) => ({
    value: customer.id,
    label: customer.long_name
  })).filter((option: { value: string }) => !selectedCustomerMap[option.value]);

  const handleInputChange = (field: string, value: string) => {
    if (isDisabled) return;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhoneInputChange = (value: string) => {
    if (isDisabled) return;
    const formatter = new PhoneNumberFormatterAsYouType('US');
    const formattedValue = formatter.input(value);
    setCurrentPhoneInput(formattedValue);
  };

  const addPhoneNumber = () => {
    if (isDisabled || !currentPhoneInput.trim()) return;

    const parsedNumber = parsePhoneNumber(currentPhoneInput, 'US');
    if (parsedNumber?.isValid()) {
      const e164Number = parsedNumber.format('E.164');
      if (!manualPhoneNumbers.includes(e164Number)) {
        setManualPhoneNumbers(prev => [...prev, e164Number]);
        setCurrentPhoneInput('');
      } else {
        toast({
          title: "Phone number already added",
          description: "",
        });
      }
    } else {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number",
      });
    }
  };

  const removePhoneNumber = (phoneNumber: string) => {
    if (isDisabled) return;
    setManualPhoneNumbers(prev => prev.filter(num => num !== phoneNumber));
  };

  const formatPhoneForDisplay = (e164Number: string) => {
    const parsedNumber = parsePhoneNumber(e164Number);
    return parsedNumber?.formatNational() || e164Number;
  };

  const handleCustomerSelect = (customerId: string) => {
    if (isDisabled) return;

    const customer = customers.find((c: Customer) => c.id === customerId);
    if (!customer) return;

    const isAlreadySelected = selectedCustomers.some(sc => sc.id === customerId);
    if (isAlreadySelected) {
      toast({
        title: "Customer already selected",
        description: "",
      });
      return;
    }

    setSelectedCustomerIds(prev => [
      ...prev,
      customerId
    ]);
    setCustomerSearchValue("");
  };

  const removeCustomer = (customerId: string) => {
    if (isDisabled) return;
    setSelectedCustomerIds(prev => prev.filter(id => id !== customerId));
  };

  const handleSubmit = async () => {
    if (isDisabled || !onSubmit) return;

    if (!formData.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for the chat stream",
      });
      return;
    }

    const participants: any[] = [];

    selectedCustomersIds.forEach((selectedCustomerId: string) => {
      participants.push({
        customer_id: selectedCustomerId
      });
    });

    manualPhoneNumbers.forEach(phoneNumber => {
      participants.push({
        sms_number: phoneNumber
      });
    });

    console.warn(selectedCustomersIds, manualPhoneNumbers)
    console.warn(participants)

    const submitData: ChatStreamFormData = {
      name: formData.name.trim(),
      create_source: ChatCreateSource.PORTAL,
      chat_type: ChatType.SOURCE_FAN_OUT_ONLY,
      message_service_env: TwilioMessageName.DestinationMessagingSid,
      participants: participants.length > 0 ? participants : [],
      customer_phone_type: SourcePhoneNumberType.Destination
    };

    await onSubmit(submitData);
  };

  const selectedPhoneSourceType = SourcePhoneNumberType.Destination;

  return (
    <div className="flex flex-col gap-4 py-4 h-full">
      {/* Two Column Layout on Large Screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left Column - Input Controls */}
        <div className="flex flex-col gap-4">
          {/* Chat Stream Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Chat Stream Name</label>
            <Input
              disabled={isDisabled}
              required
              name="name"
              value={formData.name}
              placeholder="Enter a name for the chat stream"
              onChange={(event) => handleInputChange("name", event.target.value)}
            />
          </div>

          {/* Customer Selection */}
          {mode !== 'preview' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Customers</label>
              <Combobox
                disabled={isDisabled}
                isModal
                dwimOnChange
                virtualizeList
                className="w-full"
                popoverClassName="min-w-[400px]"
                placeholder="Search and select customers..."
                value={customerSearchValue}
                onValueChange={handleCustomerSelect}
                options={customerOptions}
              />
            </div>
          )}

          {/* Manual Phone Number Input */}
          {mode !== 'preview' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Add Manual Phone Number</label>
              <div className="flex gap-2">
                <Input
                  disabled={isDisabled}
                  name="currentPhoneInput"
                  placeholder="Enter phone number"
                  value={currentPhoneInput}
                  onChange={(event) => handlePhoneInputChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addPhoneNumber();
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addPhoneNumber}
                  disabled={isDisabled || !currentPhoneInput.trim()}
                  variant="outline"
                  className="px-3"
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Selected Items */}
        <div className="flex flex-col gap-2 min-h-0 max-h-[600px]">
          <label className="text-sm font-medium">
            {mode === 'preview' ? 'Participants' : 'Selected Participants'} ({selectedCustomers.length + manualPhoneNumbers.length})
          </label>
          <div className="border rounded-md overflow-hidden flex-1 min-h-0">
            <div className="overflow-y-auto h-full max-h-[400px] lg:max-h-[500px]">
              {/* Selected Customers */}
              {selectedCustomers.length > 0 && (
                <div className="border-b">
                  <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-700">
                    CUSTOMERS ({selectedCustomers.length})
                  </div>
                  {selectedCustomers.map((selectedCustomer) => {

                    const phone_numbers = selectedCustomer.source_phone_numbers?.filter((wrapper) => wrapper.source_type === selectedPhoneSourceType) || [];
                    return (
                      <div key={`selectedCustomerPreview_${selectedCustomer.id}`} className="flex items-start justify-between py-2 px-3 border-b last:border-b-0 hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{selectedCustomer.long_name}</div>
                          {phone_numbers.length > 0 && (
                            <div className="text-xs text-gray-600 mt-0.5 truncate">
                              {phone_numbers.map(wrapper => formatPhoneForDisplay(wrapper.phone_number)).join(", ")}
                            </div>
                          )}
                        </div>
                        {!isDisabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCustomer(selectedCustomer.id)}
                            className="h-6 w-6 p-0 ml-2 flex-shrink-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Manual Phone Numbers */}
              {manualPhoneNumbers.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-700">
                    MANUAL PHONE NUMBERS ({manualPhoneNumbers.length})
                  </div>
                  {manualPhoneNumbers.map((phoneNumber, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 border-b last:border-b-0 hover:bg-gray-50">
                      <span className="text-sm truncate flex-1 min-w-0">{formatPhoneForDisplay(phoneNumber)}</span>
                      {!isDisabled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePhoneNumber(phoneNumber)}
                          className="h-6 w-6 p-0 ml-2 flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {selectedCustomers.length === 0 && manualPhoneNumbers.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
                  {mode === 'preview' ? 'No participants' : 'No participants selected yet'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {mode !== 'preview' && onSubmit && (
        <div className="flex justify-end gap-2">
          <Button onClick={handleSubmit} disabled={isDisabled}>
            {isSubmitting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <span>{mode === 'create' ? 'Create Chat Stream' : 'Update Chat Stream'}</span>
            )}
          </Button>
        </div>
      )}

      {/* Additional content slot */}
      {children}
    </div>
  );
}
